import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from 'components/Loader';
import LoadingPopup from 'components/LoadingPopup';
import Popup from 'components/Popup';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import useStudySession from 'hooks/useStudySession';
import { DISABLE_TEST_REPORT } from 'global/constants';
import { setError } from 'store/error';
import { createTimerWorker } from 'workers/createTimerWorker';
import DigitalSHSATModule from './Module';
import { AdaptiveModuleInner } from './AdaptiveModule/AdaptiveModule';
import { AdaptiveSubmitGate } from './AdaptiveModule/AdaptiveSubmitGate';
import {
    orderByChoice,
    orderByProgress,
    hasSectionChoice,
    saveStartSection,
    readStartSection,
    clearStartSection,
    StartSection,
} from './sectionOrder';
import {
    AdaptiveTestStart,
    AdaptiveDirections,
    AdaptiveSectionEnd,
    AdaptiveEndOfTest,
} from './AdaptiveModule/AdaptiveFrames';
import { ContrastSettingsModal } from './ContrastSettingsModal';
import {
    applyShsatTheme,
    saveShsatTheme,
    readShsatTheme,
    clearShsatTheme,
    DEFAULT_SHSAT_THEME,
    ShsatThemeId,
} from './shsatTheme';
import './themes.css';
import './index.css';

function DigitalSHSAT() {
    const [selectedReview, setSelectedReview] = useState('');
    const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [start, setStart] = useState(true);
    const [modules, setModules] = useState([]);
    // When true, the fixed-form player restricts navigation to
    // adaptive-style (within-passage back only, passage lock on advance,
    // navigator + Review hidden). Driven by Test.lockNavigation.
    const [lockNavigation, setLockNavigation] = useState(false);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [testName, setTestName] = useState(null);
    const [testReview, setTestReview] = useState(true);
    const [testNumber, setTestNumber] = useState(null);
    const [studentName, setStudentName] = useState(null);
    const [currentModule, setCurrentModule] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [moduleTime, setModuleTime] = useState(null);
    const [questionTime, setQuestionTime] = useState(null);
    const [notes, setNotes] = useState(null);
    const [exitOpen, setExitOpen] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [indexFilters, setIndexFilters] = useState({});
    const [adaptive, setAdaptive] = useState<{
        modules: Array<{
            id: number;
            name: string;
            sectionName: string;
            duration: number | null;
            prompt: string;
            slotCount: number;
            // First TEST-GLOBAL slot number of this module (slot numbers are
            // global: ELA 1..N, Math N+1..2N in BACKEND order). Computed from
            // the backend-ordered slot counts BEFORE any flow reordering, so
            // the progress bar can convert a global slot number back into a
            // flow position when the student chose Math first.
            firstSlotNumber: number;
        }>;
    } | null>(null);
    const [adaptiveModuleIndex, setAdaptiveModuleIndex] = useState(0);
    // Module ids observed to carry an ELA "Stand alone items" block during play
    // (navigability.standaloneItems went non-null). Drives the precise end-of-
    // section title ("End of ELA – Stand alone items" only when a block exists),
    // matching the legacy ``isElaSection && standaloneBlocks.length > 0`` check.
    const [modulesWithStandalone, setModulesWithStandalone] = useState<Set<string>>(new Set());
    // "Start with ELA or Math first" choice. Default ELA (current behavior);
    // only editable on the fresh-start screen, applied by reordering modules.
    const [startSection, setStartSection] = useState<StartSection>('ela');
    // Contrast theme ("Change the background and foreground color" tool).
    // Applied to <html> via a data attribute (themes.css); persisted per
    // test. ``theme`` is the live/previewed value; ``themeBeforeModal`` is
    // the value to revert to on Cancel.
    const [theme, setTheme] = useState<ShsatThemeId>(DEFAULT_SHSAT_THEME);
    const [themeBeforeModal, setThemeBeforeModal] = useState<ShsatThemeId>(DEFAULT_SHSAT_THEME);
    const [contrastOpen, setContrastOpen] = useState(false);
    // Phase machine for the adaptive flow's framing screens:
    //   'test-start'   — Image #17: total Q count + duration + Start button
    //   'directions'   — Image #18/#20: per-module prompt + Start button
    //   'in-module'    — slot-by-slot question rendering
    //   'section-end'  — per-section "End of {section}" screen shown when a
    //                    NON-final module finishes; Continue advances to the
    //                    next module's directions
    //   'end-of-test'  — Image #19: per-section end + Submit Final Answers
    //                    (the LAST module's end-of-section screen)
    //   'submitting'   — the existing AdaptiveSubmitGate (fires POST and
    //                    navigates to /report/digital-shsat/<id>)
    const [adaptivePhase, setAdaptivePhase] = useState<
        'test-start' | 'directions' | 'in-module' | 'section-end' | 'end-of-test' | 'submitting'
    >('test-start');
    // Mirrors the legacy 200ms loading-popup transition between
    // questionNumber changes (Module/index.tsx:1237-1241). Applied
    // whenever we cross from directions → in-module (test feels like
    // it's "loading" the first question) or between modules.
    const [adaptiveLoading, setAdaptiveLoading] = useState(false);

    const { user } = useAppSelector(state => state.user);
    const { organization } = useAppSelector(state => state.organization);
    const endTestRef = useOutsideClickHandler(() => setExitOpen(false));
    const submitRef = useOutsideClickHandler(() => setSubmitOpen(false));
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { testId } = useParams();

    const timerWorkerRef = useRef(null);

    useStudySession({
        activityType: 'test',
        activityIds: { studentTestId: testId },
    });

    const getTests = () => {
        setIsLoading(true);
        setModuleTime(null);
        axios.get(`tests/digital/shsat/${testId}/`)
            .then(res => {
                if (res.data) {
                    // Adaptive short-circuit: the backend returns a
                    // lightweight metadata payload for adaptive tests;
                    // we delegate to the per-slot AdaptiveModule. The
                    // parent (this component) tracks which module is
                    // currently active and advances on completion.
                    if (res.data.adaptive && res.data.modules?.length) {
                        const backendModules: Array<{
                            id: number;
                            name: string;
                            sectionName: string;
                            duration: number | null;
                            prompt?: string;
                            slotCount?: number;
                            complete?: boolean;
                            started?: boolean;
                        }> = res.data.modules;
                        // Slot numbers are TEST-GLOBAL in BACKEND (sectionNumber)
                        // order; stamp each module's first slot number before any
                        // flow reordering so the top bar can convert a global slot
                        // back into a flow position for the progress bar.
                        let cumulativeSlots = 0;
                        const stampedModules = backendModules.map((m) => {
                            const firstSlotNumber = cumulativeSlots + 1;
                            cumulativeSlots += m.slotCount ?? 0;
                            return { ...m, firstSlotNumber };
                        });
                        // Reconstruct FLOW order. Prefer the localStorage-saved
                        // choice (written when the student pressed Start), which
                        // survives a refresh even BEFORE any slot was served.
                        // Without a saved choice (older sessions), fall back to
                        // deriving the order from progress: two stable passes —
                        // touched-first, then complete-first — so a finished-Math
                        // + started-ELA resume puts Math (the true first section)
                        // at index 0. Fresh start keeps backend order; the
                        // explicit choice is applied at onStart below.
                        const savedStartSection = testId ? readStartSection(testId) : null;
                        if (savedStartSection) setStartSection(savedStartSection);
                        const rawModules = savedStartSection
                            ? orderByChoice(stampedModules, savedStartSection)
                            : orderByProgress(
                                orderByProgress(stampedModules, (m) => !!(m.complete || m.started)),
                                (m) => !!m.complete,
                            );
                        setAdaptive({
                            modules: rawModules.map((m) => ({
                                id: m.id,
                                name: m.name,
                                sectionName: m.sectionName,
                                duration: m.duration,
                                prompt: m.prompt ?? '',
                                slotCount: m.slotCount ?? 0,
                                firstSlotNumber: m.firstSlotNumber,
                            })),
                        });
                        // Resume entry point. Land the student on the first
                        // module they haven't finished, skipping the
                        // test-start and the already-completed sections'
                        // directions screens. Mid-section resume goes
                        // straight to the questions; a not-yet-started next
                        // section shows its directions first.
                        const firstIncomplete = rawModules.findIndex((m) => !m.complete);
                        const anyProgress = rawModules.some((m) => m.complete || m.started);
                        if (firstIncomplete === -1) {
                            // Everything answered — straight to the submit screen.
                            setAdaptiveModuleIndex(rawModules.length - 1);
                            setAdaptivePhase('end-of-test');
                        } else {
                            setAdaptiveModuleIndex(firstIncomplete);
                            if (!anyProgress) {
                                setAdaptivePhase('test-start');
                            } else if (rawModules[firstIncomplete].started) {
                                setAdaptivePhase('in-module');
                            } else {
                                setAdaptivePhase('directions');
                            }
                        }
                        setTestName(res.data.testName);
                        setTestNumber(res.data.testNumber);
                        setStudentName(res.data.studentName);
                        setNotes(res.data.notes);
                        setIsLoading(false);
                        return;
                    }
                    setLockNavigation(!!res.data.lockNavigation);
                    setAnswers(res.data.modules.flatMap(module => module.answers));
                    setTestReview(res.data.testReview);
                    setTestName(res.data.testName);
                    setTestNumber(res.data.testNumber);
                    setStudentName(res.data.studentName);
                    setNotes(res.data.notes);
                    setQuestionTime(0);
                    setIsLoading(false);

                    // Resume support. Prefer the locally autosaved answers/time for
                    // this test, then restore the exact question (and section) the
                    // student was on so a refresh lands them in place instead of back
                    // at the start screen.
                    let effectiveModules = res.data.modules;
                    let effectiveModuleTime = (res.data.modules.reduce((sum, module) => sum + (module.duration || 0), 0)) || null;
                    const savedAutosave = localStorage.getItem('autosave');
                    if (savedAutosave) {
                        const autosave = JSON.parse(savedAutosave);
                        if (testId === autosave.testId) {
                            effectiveModules = autosave.modules;
                            effectiveModuleTime = autosave.moduleTime;
                        }
                    }
                    // Reconstruct FLOW order — the legacy player's navigation is
                    // positional (modules[0] -> modules[1]), so a Math-first
                    // student must resume with Math at index 0. Prefer the
                    // localStorage-saved choice (written at beginTest), which
                    // survives a refresh even before any answer exists. Without
                    // a saved choice (older sessions), derive the order from
                    // progress: sections with any recorded work come first,
                    // fully-answered sections ahead of partially-worked ones
                    // (two stable orderByProgress passes). An untouched test
                    // keeps the backend ELA-first order.
                    const savedStartSection = testId ? readStartSection(testId) : null;
                    if (savedStartSection) setStartSection(savedStartSection);
                    if (savedStartSection) {
                        effectiveModules = orderByChoice(effectiveModules, savedStartSection);
                    } else {
                        const answerTouched = (ans: any) => !(
                            ans == null || ans === '' || ans.length === 0 ||
                            ((Array.isArray(ans) && Array.isArray(ans[0])) &&
                                (ans as [number, string][]).filter(([, val]) => val !== '').length === 0)
                        );
                        const moduleTouched = (m: any) => !!m.answers?.some((a: any) =>
                            answerTouched(a.answer) || a.timeSpent > 0 || a.marked);
                        const moduleAnswered = (m: any) => m.answers?.length > 0 &&
                            m.answers.every((a: any) => answerTouched(a.answer));
                        effectiveModules = orderByProgress(
                            orderByProgress(effectiveModules, moduleTouched),
                            moduleAnswered,
                        );
                    }
                    setModules(effectiveModules);
                    setModuleTime(effectiveModuleTime);
                    setCurrentModule(effectiveModules[0]);
                    setQuestionNumber(0);

                    const savedPosition = localStorage.getItem('shsat_position');
                    if (savedPosition) {
                        const position = JSON.parse(savedPosition);
                        if (position.testId === testId && position.started) {
                            const resumeModule = effectiveModules.find(m => m.id === position.currentModuleId);
                            if (resumeModule) {
                                setCurrentModule(resumeModule);
                                setQuestionNumber(position.questionNumber);
                                setStart(false);
                            }
                        }
                    }
                }
                else {
                    if (!testReview || DISABLE_TEST_REPORT.includes(organization.alias)) {
                        navigate('/tests/digital-shsat');
                    }
                    else {
                        navigate(`/report/digital-shsat/${testId}`);
                    }
                    
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    const sendReport = () => {
        axios.post('debug/', {
            email: user.email,
            message: JSON.stringify({
                // module: `${testName} ${moduleName}`,
                answers: answers,
                moduleTime: moduleTime
            })
        });
    }

    const beginTest = () => {
        // Apply the "start with ELA or Math first" choice by physically
        // reordering the modules into FLOW order (the positional
        // modules[0] -> modules[1] navigation assumes flow order). 'ela'
        // is a no-op — the backend already sends ELA first. Persist the
        // confirmed choice (localStorage, per test) so a refresh before
        // any answer exists still restores the chosen order.
        if (testId) saveStartSection(testId, startSection);
        const orderedModules = orderByChoice(modules, startSection);
        setModules(orderedModules);
        setStart(false);
        setQuestionTime(0);
        setCurrentModule(orderedModules[0]);
        setQuestionNumber(0);
        window.scrollTo(0, 0);
    }

    const autoSave = (updatedModules: any[], notes?: string) => {
        localStorage.setItem('autosave', JSON.stringify({
            // moduleId: moduleId,
            testId: testId,
            modules: updatedModules,
            moduleTime: moduleTime,
            notes: notes,
        }));
    }

    // Persist the current position separately from the answer autosave so a page
    // refresh resumes on the same question (and section) instead of the start
    // screen. Skipped while loading and for adaptive tests (server-driven resume).
    // Cleared on submit/discard alongside the autosave.
    useEffect(() => {
        if (isLoading || adaptive) return;
        localStorage.setItem('shsat_position', JSON.stringify({
            testId,
            questionNumber,
            currentModuleId: currentModule?.id ?? null,
            started: !start,
        }));
    }, [questionNumber, currentModule, start, isLoading, adaptive, testId]);

    const navigateQuestion = (index: number, moduleId?: string) => {
        handlePopup(false);
        // update questionTime before moving to next question
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    return {
                        ...item,
                        timeSpent: item.timeSpent + questionTime
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
        setQuestionTime(0);
        // update questionNumber, currentModule
        let newModule = currentModule;
        if (moduleId) {
            newModule = modules.find(module => module.id === moduleId);
            setCurrentModule(newModule);
        }
        setQuestionNumber(index);
        // Section-boundary handling. ``modules`` is in FLOW order (the
        // student's chosen first section is index 0 — see beginTest /
        // orderByChoice and the resume orderByProgress), so the rules key
        // on each module's ACTUAL question numbers instead of assuming
        // ELA's 1..N always sits at index 0. For the default ELA-first
        // order every branch below reduces to the original length-based
        // arithmetic.
        const firstModule = modules[0];
        const secondModule = modules[1];
        const firstFirstNum = firstModule.questions[0].questionNum;
        const firstLastNum = firstModule.questions[firstModule.questions.length - 1].questionNum;
        const secondFirstNum = secondModule.questions[0].questionNum;
        const secondLastNum = secondModule.questions[secondModule.questions.length - 1].questionNum;
        // END-screen sentinel: one past the HIGHEST question number in the
        // test, so it never collides with a real question regardless of
        // which section the flow visits last.
        const endSentinel = Math.max(firstLastNum, secondLastNum) + 1;
        if (newModule===firstModule) {
            if (index===1) setQuestionNumber(firstFirstNum); // next from the intro
            if (index===firstFirstNum - 1 && firstFirstNum !== 1) setQuestionNumber(0); // back from the first question -> intro
            if (index === firstLastNum + 1) { // finished the first section -> second section's intro (reset)
                setCurrentModule(secondModule)
                setQuestionNumber(0)
            }
        }
        if (newModule===secondModule) {
            if (index===1) setQuestionNumber(secondFirstNum); // next from the intro
            if (index===-1) { // back from the intro -> last question of the first section
                setQuestionNumber(firstLastNum);
                setCurrentModule(firstModule)
            }
            if (index===secondFirstNum - 1 && secondFirstNum !== 1) setQuestionNumber(0); // back from the first question -> intro
            if (index===secondLastNum + 1) setQuestionNumber(endSentinel); // finished the test -> END screen
        }
    }

    const navigateQuestionOneSubject = (index: number, moduleId?: string) => {
        handlePopup(false);
        // update questionTime before moving to next question
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    return {
                        ...item,
                        timeSpent: item.timeSpent + questionTime
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
        setQuestionTime(0);
        // update questionNumber, currentModule
        let newModule = currentModule;
        if (moduleId) {
            newModule = modules.find(module => module.id === moduleId);
            setCurrentModule(newModule);
        }

        setQuestionNumber(index);
        // if it's english
        if (modules?.find((module) => module.subject === 'English Language Arts')) {
            if (index===-1) { // back
                setQuestionNumber(0);
            }
        }
        // if it's math
        else if (modules?.find((module) => module.subject === 'Mathematics')) {
            if (index===1) setQuestionNumber(newModule.questions[0].questionNum); // next
            // Back from the first Math question returns to the intro. "Before the
            // first question" = firstQuestionNum - 1 (50 for the 51–100 continuous
            // numbering; 57 for the legacy 58–114 format). Was hardcoded to 57,
            // which collided with real Q57 in the new numbering and reset mid-test.
            if (index === newModule.questions[0].questionNum - 1) { // back
                setQuestionNumber(0);
            }
        }
    }

    const setChoice = (answer: string) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    if (!Array.isArray(item.answer)) {
                        const alreadySelected = item.answer == answer;
                        return {
                            ...item,
                            answer: alreadySelected ? '' : answer
                        };
                    } else{
                        const alreadySelected = item.answer.includes(answer);
                        const maxCount = module.questions.find((question)=> question.questionNum === item.questionNum).choices.filter((choice)=> choice.isCorrect).length;
                        if (!alreadySelected && item.answer.length >= maxCount) {
                            handlePopup(true);
                        }
                        return {
                            ...item,
                            answer: alreadySelected ? item.answer.filter((value)=>value!==answer) : (item.answer.length >= maxCount? [...item.answer] : [...item.answer, answer])
                        };
                    }
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const handleHotText = (text: string, index: number) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    const alreadySelected = item.answer.includes(index);
                    const maxCount = module.questions.find((question)=> question.questionNum === item.questionNum).hotText.filter((ht)=> ht.isCorrect).length;
                    if (!alreadySelected && item.answer.length >= maxCount) {
                        if (maxCount >= 2) handlePopup(true);
                    }
                    return {
                        ...item,
                        answer: alreadySelected ? item.answer.filter((value)=>value!==index) : (item.answer.length >= maxCount? (maxCount >= 2? [...item.answer]: [index]) : [...item.answer, index])
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const handleFillintheBlank = (value: string, index: number) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;
            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    let newAnswer: [number, string][] = [];
                    if (item.answer.length === 0) {
                        newAnswer = [[index, value]];
                    } else {
                        let found = false;
                        newAnswer = item.answer.map(([idx, ans]: [number, string]) => {
                            if (idx === index) {
                                found = true;
                                return [idx, value]; 
                            }
                            return [idx, ans];
                        });
                        if(!found) newAnswer.push([index, value]);
                    }
                    return {
                        ...item,
                        answer: newAnswer,
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const handleInlineChoice = (index: number, answer: string) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;
            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    let newAnswer: [number, string][] = [];
                    if (item.answer.length === 0) {
                        newAnswer = [[index, answer]];
                    } else {
                        let found = false;
                        newAnswer = item.answer.map(([idx, ans]: [number, string]) => {
                            if (idx === index) {
                                found = true;
                                return [idx, answer]; 
                            }
                            return [idx, ans];
                        });
                        if(!found) newAnswer.push([index, answer]);
                    }
                    return {
                        ...item,
                        answer: newAnswer,
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const handleHotSpotFigure = (answer: string) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    const alreadySelected = item.answer.includes(answer);
                    const maxCount = module.questions.find((question)=> question.questionNum === item.questionNum).choices.filter((choice)=> choice.isCorrect).length;
                    if (!alreadySelected && item.answer.length >= maxCount) {
                        if (maxCount >=2) handlePopup(true);
                    }
                    return {
                        ...item,
                        answer: alreadySelected ? item.answer.filter((value)=>value!==answer) : (item.answer.length >= maxCount? (maxCount >= 2? [...item.answer]: [answer]) : [...item.answer, answer])
                    }
                    ;
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const handleEquationEditor = (value: string) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;
            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    let newAnswer = ''
                    newAnswer = value;
                    
                    return {
                        ...item,
                        answer: newAnswer,
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const crossOutChoice = (choiceId: string) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    const crossedOut = item.crossedOut || [];

                    const isAlreadyCrossed = crossedOut.includes(choiceId);
                    const updatedCrossedOut = isAlreadyCrossed
                        ? crossedOut.filter((value) => value !== choiceId) 
                        : [...crossedOut, choiceId];

                    // 만약 선택한 답이면 answer도 제거
                    let updatedAnswer;
                    if (!Array.isArray(item.answer)) {
                        updatedAnswer = choiceId === item.answer ? '' : item.answer;                    
                    } else {
                        updatedAnswer = item.answer.includes(choiceId)? item.answer.filter((value)=>value!==choiceId) : [...item.answer];   
                    }

                    return {
                        ...item,
                        answer: updatedAnswer,
                        crossedOut: updatedCrossedOut,
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const markQuestion = (questionNumber: number) => {
        const updatedModules = modules.map((module) => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map((item) => {
                if (item.questionNum === questionNumber) {
                    return {
                        ...item,
                        marked: !item.marked
                    };
                }
                return item;
            });

            return {
                ...module,
                answers: updatedAnswers,
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    }

    const generatePassageHTML = (passage, startOffset, endOffset, color) => {
        let passageHTML = '';
        let passagePreview = '';
        let lastIndex = 0;

        const addMarkTags = (node) => {
            if (node.nodeName === 'IMG') {
                passageHTML += node.outerHTML;
            }
            else if (node.outerHTML) {
                const { innerHTML, outerHTML } = node;
                const childOffset = outerHTML.indexOf('>') + 1;
                passageHTML += outerHTML.slice(0, childOffset);
                node.childNodes.forEach(addMarkTags);
                passageHTML += outerHTML.slice(childOffset + innerHTML.length);
            }
            else {
                const { textContent } = node;
                if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex);
                    passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                }
                else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(startOffset - lastIndex);
                    passageHTML += '</mark>';
                    passagePreview += textContent.slice(startOffset - lastIndex);
                }
                else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(0, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex);
                    passagePreview += textContent.slice(0, endOffset - lastIndex);
                }
                else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                    passageHTML += `<mark>`;
                    passageHTML += textContent;
                    passageHTML += '</mark>';
                    passagePreview += textContent;
                }
                else {
                    passageHTML += textContent;
                }
                lastIndex += textContent.length;
            }
        };

        passage.childNodes.forEach(addMarkTags);
        return { passageHTML, passagePreview};
    };

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
    }) => {
        const updatedModules = modules.map(module => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map(answerItem => {
                if (answerItem.questionNum !== questionNumber) return answerItem;
                const updatedAnnotations = [...answerItem.annotations];
                let extraAnnotations = [];

                // update annotation
                updatedAnnotations.forEach((ann, index) => {
                    if (ann.endIndex > annotation.startIndex && annotation.endIndex >=ann.endIndex) {
                        updatedAnnotations[index].endIndex = annotation.startIndex
                    } else if (ann.startIndex < annotation.endIndex && annotation.startIndex <= ann.startIndex) {
                        updatedAnnotations[index].startIndex = annotation.endIndex
                    } else if (annotation.startIndex > ann.startIndex && annotation.endIndex < ann.endIndex) {
                        const passage = document.getElementById(ann.passageId);
                        if (!passage) return;
                        const {passageHTML, passagePreview} = generatePassageHTML(passage, annotation.endIndex, ann.endIndex, updatedAnnotations[index].color);

                        extraAnnotations.push({
                            passageId: annotation.passageId,
                            startIndex: annotation.endIndex,
                            endIndex: ann.endIndex,
                            html: passageHTML,
                            preveiw: passagePreview,
                            color: ann.color
                        });
                        updatedAnnotations[index].endIndex = annotation.startIndex
                    }
                    // update passageHTML, html
                    const passage = document.getElementById(ann.passageId);
                    if (!passage) return;
                    const { passageHTML, passagePreview } = generatePassageHTML(passage, updatedAnnotations[index].startIndex, updatedAnnotations[index].endIndex, updatedAnnotations[index].color);
                    updatedAnnotations[index].passageHTML = passageHTML
                    updatedAnnotations[index].html = passageHTML
                    updatedAnnotations[index].preview = passagePreview
                })
                
                // new annotation 
                updatedAnnotations.push({
                    ...annotation
                });

                // update annotation
                extraAnnotations.forEach(extraAnn => {
                    updatedAnnotations.push({
                        ...extraAnn
                    });
                });
                return {
                    ...answerItem,
                    annotations: updatedAnnotations
                };
            });

            return {
                ...module,
                answers: updatedAnswers
            };
        });
        autoSave(updatedModules);
        setModules(updatedModules);
    };

    const deleteAnnotation = (annotation) => {
        const updatedModules = modules.map(module => {
            if (module.id !== currentModule.id) return module;

            const updatedAnswers = module.answers.map(answerItem => {
                if (answerItem.questionNum !== questionNumber) return answerItem;
                const updatedAnnotations = [...answerItem.annotations];
                let extraAnnotations = [];

                // update annotation
                updatedAnnotations.forEach((ann, index) => {
                    if (ann.endIndex > annotation.startIndex && annotation.endIndex >=ann.endIndex) {
                        updatedAnnotations[index].endIndex = annotation.startIndex
                    } else if (ann.startIndex < annotation.endIndex && annotation.startIndex <= ann.startIndex) {
                        updatedAnnotations[index].startIndex = annotation.endIndex
                    } else if (annotation.startIndex > ann.startIndex && annotation.endIndex < ann.endIndex) {
                        const passage = document.getElementById(ann.passageId);
                        if (!passage) return;
                        const {passageHTML, passagePreview} = generatePassageHTML(passage, annotation.endIndex, ann.endIndex, updatedAnnotations[index].color);

                        extraAnnotations.push({
                            passageId: annotation.passageId,
                            startIndex: annotation.endIndex,
                            endIndex: ann.endIndex,
                            html: passageHTML,
                            preveiw: passagePreview,
                            color: ann.color
                        });
                        updatedAnnotations[index].endIndex = annotation.startIndex
                    }
                    // update passageHTML, html
                    const passage = document.getElementById(ann.passageId);
                    if (!passage) return;
                    const { passageHTML, passagePreview } = generatePassageHTML(passage, updatedAnnotations[index].startIndex, updatedAnnotations[index].endIndex, updatedAnnotations[index].color);
                    updatedAnnotations[index].passageHTML = passageHTML
                    updatedAnnotations[index].html = passageHTML
                    updatedAnnotations[index].preview = passagePreview
                })

                // update annotation
                extraAnnotations.forEach(extraAnn => {
                    updatedAnnotations.push({
                        ...extraAnn
                    });
                });
                return {
                    ...answerItem,
                    annotations: updatedAnnotations
                };
            });

            return {
                ...module,
                answers: updatedAnswers
            };
        });
        autoSave(updatedModules)
        setModules(updatedModules);
    }

    const saveNotes = (value: string) =>{
        setNotes(value);
        autoSave(modules, notes);
    }

    // const handleIndexFilters = (filters: { 'All questions': number, 'Flagged': number, 'Answered': number, 'Unanswered': number }) => {
    //     setIndexFilters(filters);
    // }

    // const handleReview = (selectedReview: string) => {
    //     manageExit(false);
    //     setSelectedReview(selectedReview);
    // }

    const handleSubmit = () => {
        setStart(true);
        setSubmitOpen(false);
        setIsLoading(true);
        sendReport();
        axios.post(`tests/digital/submit/shsat/${testId}/`, {
                modules: modules,
                timeLeft: moduleTime,
                notes: notes
            })
            .then(() => {
                localStorage.removeItem('autosave');
                localStorage.removeItem('shsat_position');
                if (testId) clearStartSection(testId);
                if (testId) clearShsatTheme(testId);
                setModuleTime(null);
                setQuestionTime(null);
                if (!testReview || DISABLE_TEST_REPORT.includes(organization.alias)) {
                    navigate('/tests');
                }
                else {
                    navigate(`/report/digital-shsat/${testId}`);
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    // Keep a ref to the latest handleSubmit so the timer worker (whose onmessage
    // closure is wired only once) always submits LIVE answers, not a stale snapshot.
    const handleSubmitRef = useRef(handleSubmit);
    useEffect(() => { handleSubmitRef.current = handleSubmit; });

    const handleDiscardAndExit = () => {
        setIsLoading(true);
        // Wipe the server-saved attempt (the 5-minute background autosave and
        // any prior Save & Exit) so re-entering starts the test fresh. Clearing
        // localStorage alone left the server copy behind, which the GET then
        // resumed. Local clear + navigate happen only after the server confirms.
        axios.post(`tests/digital/discard/shsat/${testId}/`, {})
            .then(() => {
                localStorage.removeItem('autosave');
                localStorage.removeItem('shsat_position');
                if (testId) clearStartSection(testId);
                if (testId) clearShsatTheme(testId);
                navigate(-1);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }

    // Adaptive "Discard and Exit". The adaptive model has NO batched autosave —
    // every answered slot is committed at save-time — and its own resume tables
    // (SHSATAdaptiveServedQuestion), so the legacy discard endpoint doesn't
    // clear it. Hit the dedicated adaptive discard endpoint (wipes served slots
    // + answers server-side), THEN clear this attempt's localStorage (section
    // choice, theme, and the adaptive per-test timer key), THEN navigate away.
    const handleAdaptiveDiscardExit = () => {
        setIsLoading(true);
        axios.post(`tests/digital/shsat/adaptive/discard/${testId}/`, {})
            .then(() => {
                if (testId) {
                    clearStartSection(testId);
                    clearShsatTheme(testId);
                    try { localStorage.removeItem(`shsatAdaptive:timer:${testId}`); } catch { /* private browsing */ }
                }
                navigate(-1);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }

    const handleSaveAndExit = () => {
        setIsLoading(true);
        axios.post(`tests/digital/save/shsat/${testId}/`, {
                modules: modules,
                timeLeft: moduleTime,
                notes: notes
            })
            .then(() => {
                // exit
                navigate(-1);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }
    
    const handlePopup = (isOpen: boolean) => {
        setIsAlertPopupOpen(isOpen);
    }

    useEffect(()=>{
        setCurrentModule(modules.find(module => module.id===currentModule.id))
    }, [modules])

    useEffect(() => {
        getTests();
    }, []);

    // Load the persisted contrast theme (per test) once on mount so a
    // refresh mid-attempt restores the student's chosen theme.
    useEffect(() => {
        if (testId) setTheme(readShsatTheme(testId));
    }, [testId]);

    // Apply the theme to <html> (survives phase/flow changes; themes.css
    // scopes the recolor to the SHSAT UI). Cleared when leaving the test.
    useEffect(() => {
        applyShsatTheme(document.documentElement, theme);
        return () => { document.documentElement.removeAttribute('data-shsat-theme'); };
    }, [theme]);

    useEffect(() => {
        if (isLoading || start) return;

        if (timerWorkerRef.current) {
            timerWorkerRef.current.postMessage({ type: 'stop' });
            timerWorkerRef.current.terminate();
            timerWorkerRef.current = null;
        }

        timerWorkerRef.current = createTimerWorker();

        timerWorkerRef.current.onmessage = (event) => {
            if (event.data === 'tick') {
                setModuleTime(prev => {
                    if (prev === null) return prev;
                    if (prev <= 1) {
                        handleSubmitRef.current();
                        return 0;
                    }
                    return prev - 1;
                });
                setQuestionTime(prev => {
                    if (prev === null) return prev;
                    return prev + 1;
                });
            }
        };

        timerWorkerRef.current.postMessage({ type: 'start', delay: 1000 });

        return () => {
            if (timerWorkerRef.current) {
                timerWorkerRef.current.postMessage({ type: 'stop' });
                timerWorkerRef.current.terminate();
                timerWorkerRef.current = null;
            }
        };
    }, [isLoading, start]);

    /* autosave to server every 5 minutes */
    useEffect(() => {
        if (moduleTime === null || start) return;
        if (moduleTime > 0 && moduleTime % 300 === 0) {
            axios.post(`tests/digital/save/shsat/${testId}/`, {
                modules: modules,
                timeLeft: moduleTime,
                notes: notes,
            }).catch(error => {
                // console.log(error); // background autosave — swallow transient network errors
            });
        }
    }, [moduleTime, start]);

    // Contrast Settings tool. ``openContrast`` remembers the pre-modal theme
    // (for Cancel) and opens the modal; the modal live-previews via setTheme.
    const openContrast = () => {
        setThemeBeforeModal(theme);
        setContrastOpen(true);
    };
    const contrastModal = contrastOpen ? (
        <ContrastSettingsModal
            value={theme}
            onPreview={setTheme}
            onContinue={() => {
                if (testId) saveShsatTheme(testId, theme);
                setContrastOpen(false);
            }}
            onCancel={() => {
                setTheme(themeBeforeModal);
                setContrastOpen(false);
            }}
        />
    ) : null;

    if (isLoading) return <Loader />;

    // Adaptive short-circuit: render the framing screens
    // (Test Start → Directions → in-module → End-of-Test → Submit)
    // instead of the legacy bulk-load Module flow.
    if (adaptive && testId) {
        const currentModule = adaptive.modules[adaptiveModuleIndex];
        const moduleCount = adaptive.modules.length;
        const totalQuestions = adaptive.modules.reduce(
            (sum, m) => sum + (m.slotCount || 0),
            0,
        );
        const totalDurationSeconds = adaptive.modules.reduce(
            (sum, m) => sum + (m.duration ?? 0),
            0,
        ) || null;

        const handleModuleComplete = () => {
            // Module finished → per-section end screen. For a non-final
            // module it's the 'section-end' phase (Continue → next module's
            // directions); for the LAST module it's 'end-of-test' (per-
            // section title + Submit Final Answers). The module index is NOT
            // advanced here — the 'section-end' screen shows the just-
            // finished module's name and advances on Continue.
            if (adaptiveModuleIndex < moduleCount - 1) {
                setAdaptivePhase('section-end');
            } else {
                setAdaptivePhase('end-of-test');
            }
        };

        if (adaptivePhase === 'test-start') {
            return (
                <AdaptiveTestStart
                    testName={testName}
                    studentName={studentName}
                    totalQuestions={totalQuestions}
                    totalDurationSeconds={totalDurationSeconds}
                    // "Start with ELA or Math first" — only offered on the fresh
                    // test-start screen (this phase never renders on resume).
                    startSection={startSection}
                    onStartSectionChange={setStartSection}
                    showSectionChoice={hasSectionChoice(adaptive.modules)}
                    onStart={() => {
                        // Apply the choice by physically reordering the modules
                        // into FLOW order — the index-based advance
                        // (adaptiveModuleIndex + 1) assumes flow order. 'ela'
                        // keeps the backend order (already ELA first). Persist
                        // the confirmed choice (localStorage, per test) so a
                        // refresh before the first slot is served still
                        // restores the chosen order.
                        saveStartSection(testId, startSection);
                        if (startSection === 'math') {
                            setAdaptive(prev => prev
                                ? { ...prev, modules: orderByChoice(prev.modules, 'math') }
                                : prev);
                            setAdaptiveModuleIndex(0);
                        }
                        setAdaptivePhase('directions');
                    }}
                />
            );
        }
        if (adaptivePhase === 'directions') {
            // Loader flash between directions and the first question
            // — matches the legacy 200ms ``LoadingPopup`` swap (see
            // Module/index.tsx:1237-1241). Without it, the next-arrow
            // click snaps instantly to the question, which feels
            // jarring next to the legacy timing.
            const startModule = () => {
                setAdaptiveLoading(true);
                setTimeout(() => {
                    setAdaptivePhase('in-module');
                    setAdaptiveLoading(false);
                }, 200);
            };
            return (
                <>
                    <AdaptiveDirections
                        testName={testName}
                        sectionName={currentModule.sectionName}
                        studentName={studentName}
                        prompt={currentModule.prompt || currentModule.sectionName}
                        onContinue={startModule}
                    />
                    {adaptiveLoading && <LoadingPopup />}
                </>
            );
        }
        if (adaptivePhase === 'section-end') {
            // Non-final module finished → "End of {section}" screen; Continue
            // advances to the next module's directions. currentModule is the
            // just-finished module (index not yet advanced).
            return (
                <AdaptiveSectionEnd
                    testName={testName}
                    sectionName={currentModule.sectionName}
                    studentName={studentName}
                    endsOnStandalone={modulesWithStandalone.has(String(currentModule.id))}
                    onContinue={() => {
                        setAdaptiveModuleIndex((prev) => prev + 1);
                        setAdaptivePhase('directions');
                    }}
                />
            );
        }
        if (adaptivePhase === 'end-of-test') {
            return (
                <AdaptiveEndOfTest
                    testName={testName}
                    sectionName={currentModule.sectionName}
                    studentName={studentName}
                    endsOnStandalone={modulesWithStandalone.has(String(currentModule.id))}
                    onSubmit={() => {
                        // Fresh retakes start clean — drop the saved
                        // section-first choice + theme once submitted.
                        clearStartSection(testId);
                        clearShsatTheme(testId);
                        setAdaptivePhase('submitting');
                    }}
                    studentTestId={testId}
                    moduleId={String(currentModule.id)}
                    durationSeconds={currentModule.duration}
                    totalSlots={totalQuestions}
                    onDiscardExit={handleAdaptiveDiscardExit}
                />
            );
        }
        if (adaptivePhase === 'submitting') {
            return (
                <>
                    <Helmet>
                        <title>
                            {testName ? testName : 'Digital SHSAT Practice Test'} - {organization.name}
                        </title>
                    </Helmet>
                    <AdaptiveSubmitGate studentTestId={testId} />
                </>
            );
        }
        // adaptivePhase === 'in-module'
        return (
            <>
                <Helmet>
                    <title>
                        {testName ? testName : 'Digital SHSAT Practice Test'} - {organization.name}
                    </title>
                </Helmet>
                <AdaptiveModuleInner
                    // key forces a fresh mount of the hook + state on
                    // each module transition, so the new module's
                    // serve endpoint fires from a clean state machine.
                    key={currentModule.id}
                    studentTestId={testId}
                    moduleId={String(currentModule.id)}
                    testName={testName}
                    sectionName={currentModule.sectionName}
                    moduleDurationSeconds={currentModule.duration}
                    // Legacy parity: one TOTAL clock across all modules + auto-
                    // submit at 0:00. The top bar counts this down continuously
                    // (test-wide storage key); expiry jumps straight to submit.
                    totalDurationSeconds={adaptive.modules.reduce((sum, m) => sum + (m.duration || 0), 0) || null}
                    onTimeExpired={() => {
                        clearStartSection(testId);
                        clearShsatTheme(testId);
                        setAdaptivePhase('submitting');
                    }}
                    // First TEST-GLOBAL slot number of this module (backend
                    // order). Lets the top bar convert the global slot into a
                    // within-module position for the per-section "N OF M" label.
                    moduleFirstSlot={currentModule.firstSlotNumber}
                    totalSlots={totalQuestions}
                    // Per-section "N OF M" total: this module's own slot count
                    // (so Math reads "1 OF 50", not the test-global "51 OF 100").
                    moduleSlotCount={currentModule.slotCount}
                    studentName={studentName}
                    onModuleComplete={handleModuleComplete}
                    // Record that THIS module has a stand-alone block, so its
                    // end-of-section title reads "End of ELA – Stand alone items".
                    onStandaloneItemsSeen={() => {
                        const id = String(currentModule.id);
                        setModulesWithStandalone((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
                    }}
                    onOpenContrast={openContrast}
                    onDiscardExit={handleAdaptiveDiscardExit}
                />
                {contrastModal}
            </>
        );
    }

    return (
        <div className='digitalSHSAT' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    {testName ? testName : 'Digital SHSAT Practice Test'} - {organization.name}
                </title>
            </Helmet>
            <div className='digitalSHSAT__box'>
                <DigitalSHSATModule
                    start = {start}
                    startSection = {startSection}
                    setStartSection = {setStartSection}
                    notes = {notes}
                    studentName = {studentName}
                    testName = {testName}
                    testNumber = {testNumber}
                    timeLeft={moduleTime}
                    isUntimed={moduleTime===null? true: false}
                    questionNumber = {questionNumber}
                    lockNavigation = {lockNavigation}
                    currentModule = {currentModule}
                    modules = {modules}
                    isAlertPopupOpen = {isAlertPopupOpen}
                    beginTest = {beginTest}
                    handlePopup={handlePopup}
                    navigateQuestion = {modules.length === 1? navigateQuestionOneSubject: navigateQuestion}
                    handleSelect={value => setChoice(value)}
                    handleHotText={(text, index) => handleHotText(text, index)}
                    handleFillintheBlank={(value, index) => handleFillintheBlank(value, index)}
                    handleInlineChoice={(index, answer) => handleInlineChoice(index, answer)}
                    handleHotspotFigure={value => handleHotSpotFigure(value)}
                    handleEquationEditor={(value) => handleEquationEditor(value)}
                    markQuestion={value => markQuestion(value)}
                    crossOutChoice={value => crossOutChoice(value)}
                    saveAnnotation={saveAnnotation}
                    deleteAnnotation={deleteAnnotation}
                    handleSubmit = {handleSubmit}
                    handleDiscardAndExit={handleDiscardAndExit}
                    handleSaveAndExit={handleSaveAndExit}
                    saveNotes = {saveNotes}
                    autoSave={autoSave}
                    setModules={setModules}
                    onOpenContrast={openContrast}
                />
            </div>
            {contrastModal}
        </div>
    )
}

export default DigitalSHSAT;
